import { useState } from "react";
import { toast } from "sonner";
import { submitEnquiry } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { waLink, poojaWaMessage, generalWaMessage } from "@/lib/whatsapp";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Phone, Send } from "lucide-react";

const EnquiryForm = ({ poojaName = "", poojaSlug = "", onSuccess }) => {
  const site = useSite();
  const [form, setForm] = useState({
    name: "", phone: "", whatsapp: "", email: "", city: "",
    pooja_interested: poojaName, problem: "", preferred_date: "", notes: "", company: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await submitEnquiry({ ...form, pooja_slug: poojaSlug, source: "form" });
      toast.success("🙏 Thank you! Our priest team will contact you shortly.");
      setForm({ name: "", phone: "", whatsapp: "", email: "", city: "", pooja_interested: poojaName, problem: "", preferred_date: "", notes: "", company: "" });
      onSuccess && onSuccess();
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error("You've sent a few enquiries already. Please reach us directly on WhatsApp.");
      } else {
        toast.error("Something went wrong. Please try WhatsApp instead.");
      }
    } finally {
      setLoading(false);
    }
  };

  const waMsg = poojaName ? poojaWaMessage(poojaName) : generalWaMessage;

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="enquiry-form">
      {/* honeypot: hidden from users, catches bots */}
      <input
        type="text"
        name="company"
        value={form.company}
        onChange={set("company")}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
        style={{ display: "none" }}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm">Name *</Label>
          <Input data-testid="enquiry-name" value={form.name} onChange={set("name")} placeholder="Your full name" className="mt-1" />
        </div>
        <div>
          <Label className="text-sm">Phone *</Label>
          <Input data-testid="enquiry-phone" value={form.phone} onChange={set("phone")} placeholder="Mobile number" className="mt-1" />
        </div>
        <div>
          <Label className="text-sm">WhatsApp Number</Label>
          <Input data-testid="enquiry-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} placeholder="If different from phone" className="mt-1" />
        </div>
        <div>
          <Label className="text-sm">Email</Label>
          <Input data-testid="enquiry-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="mt-1" />
        </div>
        <div>
          <Label className="text-sm">City / Country</Label>
          <Input data-testid="enquiry-city" value={form.city} onChange={set("city")} placeholder="e.g. Bengaluru" className="mt-1" />
        </div>
        <div>
          <Label className="text-sm">Pooja Interested In</Label>
          <Input data-testid="enquiry-pooja" value={form.pooja_interested} onChange={set("pooja_interested")} placeholder="Pooja name" className="mt-1" />
        </div>
        <div>
          <Label className="text-sm">Preferred Date</Label>
          <Input data-testid="enquiry-date" type="date" value={form.preferred_date} onChange={set("preferred_date")} className="mt-1" />
        </div>
        <div>
          <Label className="text-sm">Problem / Purpose</Label>
          <Input data-testid="enquiry-problem" value={form.problem} onChange={set("problem")} placeholder="e.g. marriage delay, health" className="mt-1" />
        </div>
      </div>
      <div>
        <Label className="text-sm">Additional Notes</Label>
        <Textarea data-testid="enquiry-notes" value={form.notes} onChange={set("notes")} placeholder="Anything you'd like us to know" className="mt-1" rows={3} />
      </div>
      <div className="flex flex-col sm:flex-row gap-3 pt-1">
        <button
          type="submit"
          disabled={loading}
          data-testid="enquiry-submit"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 font-semibold text-white hover:bg-[#CC5204] transition-colors disabled:opacity-60"
        >
          <Send className="h-4 w-4" /> {loading ? "Sending..." : "Submit Enquiry"}
        </button>
        <a
          href={waLink(site.whatsapp_number, waMsg)}
          target="_blank"
          rel="nofollow noopener noreferrer"
          data-testid="enquiry-whatsapp-cta"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-whatsapp px-6 py-3 font-semibold text-white hover:opacity-90 transition-opacity"
        >
          <Phone className="h-4 w-4" /> Book on WhatsApp
        </a>
      </div>
    </form>
  );
};

export default EnquiryForm;
